import { FC, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { InputRef } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { match } from 'ts-pattern';
import { ISearchTag } from '@modules/search/domain/models/searchTag.model';
import { ISuggestSearch } from '@modules/search/domain/models/suggestSearch.model';
import { useScreen } from '@shared/helpers/screen.helper';
import { ModuleType } from '@shared/types/module.type';
import ResultGrid from './ResultGrid';
import { CloseIcon, Container, SearchBoxLayout, SearchInput } from './SearchBox.style';
import SuggestSearch from './suggestSearch/SuggestSearch';
interface Props {
  popularSearch: ISearchTag[];
  recentlySearch: string[];
  setRecentlySearch: (val: any) => void;
  onSearchSubmit: (keyword: string, category: string) => void;
  suggestSearch: ISuggestSearch;
  isSearchModalOpen: boolean;
  setIsSearchModalOpen: (val: boolean) => void;
  searchValue: string;
  setSearchValue: (val: string) => void;
}
export type RecentlySearchList = Omit<ISearchTag, 'id'>;
const SearchBox: FC<Props> = ({
  recentlySearch,
  popularSearch,
  setRecentlySearch,
  onSearchSubmit,
  suggestSearch,
  isSearchModalOpen,
  setIsSearchModalOpen,
  searchValue,
  setSearchValue
}: Props) => {
  const router = useRouter();
  const searchModalRef = useRef(null);
  const {
    isDesktop
  } = useScreen();
  const [recentlySearchList, setRecentlySearchList] = useState<RecentlySearchList[]>([]);
  const searchInputId = document.getElementById('mainSearchInput');
  const suggestSearchIsNull = suggestSearch.products.length === 0 && suggestSearch.recipe.length === 0 && suggestSearch.tipsTricks.length === 0;
  useEffect(() => {
    mapRecentlySearchPath();
  }, [recentlySearch, router]);
  const mapRecentlySearchPath = () => {
    const category = mapSearchRoute();
    const searchList = recentlySearch.map(text => {
      return {
        linkOut: `/search?q=${text}&c=${category}`,
        text
      };
    });
    setRecentlySearchList(searchList);
  };
  const mapSearchRoute = (): ModuleType => {
    const {
      asPath
    } = router;
    const currentPath = asPath.split('/')[1].toLowerCase();
    return match(currentPath).with('product', () => ModuleType.Product).with('tips-tricks', () => ModuleType.TipsTricks).otherwise(() => ModuleType.Recipe);
  };
  const onSearchValue = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const category = mapSearchRoute();
      if (searchValue === '') {
        router.push(`/search?&c=${category}`);
        if (router.route === '/search') {
          onSearchSubmit('', category);
        }
      } else {
        const trimSearch = searchValue.trim();
        const resultSearch = [trimSearch, ...recentlySearch];
        const maxSearchValue = 50;
        const uniqueResult = resultSearch.filter((item, index) => resultSearch.indexOf(item) === index).slice(0, maxSearchValue);
        setRecentlySearch(uniqueResult);
        router.push(`/search?q=${trimSearch}&c=${category}`);
        if (router.route === '/search') {
          onSearchSubmit(trimSearch, category);
        }
      }
      setSearchValue('');
      setIsSearchModalOpen(false);
    }
  };
  const searchInput = useCallback((inputElement: InputRef) => {
    if (inputElement && isSearchModalOpen) inputElement.focus();
  }, [isSearchModalOpen]);
  const onSelectedTag = (shouldReloadPage: boolean) => {
    setIsSearchModalOpen(false);
    if (router.route === '/search') {
      if (shouldReloadPage) {
        setTimeout(() => {
          router.reload();
        }, 50);
      }
    }
  };
  const onCloseWhenTouchMove = () => {
    setIsSearchModalOpen(false);
    if (searchInputId) searchInputId.style.display = 'none';
  };
  return <AnimatePresence>
            {isSearchModalOpen && <SearchBoxLayout transition={{
      duration: 0.75,
      type: 'spring'
    }} initial={{
      opacity: 0,
      top: -100
    }} animate={{
      opacity: 1,
      top: isDesktop ? 72 : 0
    }} exit={{
      opacity: 0,
      top: -100
    }}>
                    <Container onTouchMove={onCloseWhenTouchMove} ref={searchModalRef}>
                        <CloseIcon src="/icons/x.svg" alt="Close icon" onClick={onCloseWhenTouchMove} width={18} height={18} />
                        <SearchInput id="mainSearchInput" ref={searchInput} value={searchValue} onChange={e => setSearchValue(e.target.value)} onKeyDown={e => onSearchValue(e)} placeholder="ค้นหาใน CP Brandsite" prefix={<Image src="/icons/search.svg" alt="search" width={24} height={24} />} suffix={<motion.div className="suffixIcon" onClick={() => setSearchValue('')} animate={searchValue === '' ? 'hide' : 'show'} variants={{
          show: {
            opacity: 1
          },
          hide: {
            opacity: 0
          }
        }}>
                                    <Image src="/icons/x.svg" alt="Close icon" width={32} height={32} />
                                </motion.div>} autoFocus />

                        {searchValue.length === 0 || suggestSearchIsNull ? <ResultGrid recentlySearch={recentlySearchList} popularSearch={popularSearch} onSelectedTag={onSelectedTag} /> : <SuggestSearch searchValue={searchValue} suggestSearch={suggestSearch} setIsSearchModalOpen={setIsSearchModalOpen} />}
                    </Container>
                </SearchBoxLayout>}
        </AnimatePresence>;
};
export default SearchBox;